import { useCallback, useEffect, useRef, useState } from 'react';

import { ItemsQuery } from '@/types/ItemsQuery';

import {
    BrowseActiveFilter,
    FILTER_TYPE,
    FILTER_URL_KEYS,
    FilterUrlKey,
} from '../types';
import {
    notifyAngularItemSearchQueryChanged,
    readFilterParamsFromUrl,
    readKeywordFromUrl,
    writeFilterParamsToUrl,
    writeKeywordToUrl,
} from '../utils/urlSearchParam';

export interface UseBrowseQueryResult {
    query: ItemsQuery;
    setQuery: (
        update: Partial<ItemsQuery> | ((previous: ItemsQuery) => ItemsQuery),
    ) => void;
    activeFilters: Record<string, BrowseActiveFilter>;
    setActiveFilter: (type: string, filter: BrowseActiveFilter) => void;
    removeFilter: (filter: BrowseActiveFilter) => void;
    applyBulkSearch: (bulkSearchId: string) => void;
}

const MUTUALLY_EXCLUSIVE_TYPES: Record<string, string> = {
    [FILTER_TYPE.USER_DEFINED]: FILTER_TYPE.DATA_MODEL_DEFINED,
    [FILTER_TYPE.DATA_MODEL_DEFINED]: FILTER_TYPE.USER_DEFINED,
    [FILTER_TYPE.COMPLIANT_ITEMS]: FILTER_TYPE.NON_COMPLIANT_ITEMS,
    [FILTER_TYPE.NON_COMPLIANT_ITEMS]: FILTER_TYPE.COMPLIANT_ITEMS,
};

type AngularInjector = {
    get: (name: string) => unknown;
};

/**
 * One in-flight save. The nav link is `/browse?selectionId` with no id;
 * a second route event before the POST returns must not create two selections.
 */
let watchlistSelectionSaveStarted = false;

function angularInjector(): AngularInjector | null {
    try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const angularGlobal = (window as any).angular;
        return angularGlobal?.element(document.body)?.injector?.() ?? null;
    } catch {
        return null;
    }
}

/**
 * `true` means the Watchlist link (`/browse?selectionId`, no value).
 * A string is a selection id already saved by SelectionResource.
 * ItemsController is not started when NEXT_HIT_LIST is on, so this hook
 * has to turn the bare flag into an id (fetch-item-controller.js#loadWatchlistItems).
 */
function readRouteSelectionId(): string | true | undefined {
    const routed = (angularInjector()?.get('$routeParams') as { selectionId?: unknown } | undefined)
        ?.selectionId;
    if (routed === true) {
        return true;
    }
    if (typeof routed === 'string' && routed !== '' && routed !== 'true') {
        return routed;
    }

    const params = new URLSearchParams(window.location.search);
    if (!params.has('selectionId')) {
        return undefined;
    }
    const value = params.get('selectionId');
    if (!value || value === 'true') {
        return true;
    }
    return value;
}

function writeSelectionId(selectionId: string | null): void {
    const injector = angularInjector();
    const $location = injector?.get('$location') as {
        search: (key: string, value?: string | null) => void;
    } | undefined;
    const $rootScope = injector?.get('$rootScope') as {
        $$phase?: string;
        $apply?: (fn: () => void) => void;
    } | undefined;
    if (!$location) {
        return;
    }
    const write = () => $location.search('selectionId', selectionId);
    if ($rootScope?.$apply && !$rootScope.$$phase) {
        $rootScope.$apply(write);
        return;
    }
    write();
}

/** POST the watchlist keys and replace `?selectionId` with the saved id. */
function saveBareWatchlistSelection(): void {
    if (watchlistSelectionSaveStarted) {
        return;
    }
    const injector = angularInjector();
    if (!injector) {
        return;
    }
    const watchlist = injector.get('WatchlistService') as {
        getWatchlistPrimaryKeys?: () => string[];
    } | undefined;
    const primaryKeys = watchlist?.getWatchlistPrimaryKeys?.() ?? [];
    if (primaryKeys.length === 0) {
        writeSelectionId(null);
        return;
    }
    const selectionResource = injector.get('SelectionResource') as {
        save?: (
            body: { primaryKeys: string[] },
            success: (response: { selectionId?: string }) => void,
            error?: () => void,
        ) => void;
    } | undefined;
    if (!selectionResource?.save) {
        return;
    }
    watchlistSelectionSaveStarted = true;
    selectionResource.save(
        { primaryKeys },
        (response) => {
            watchlistSelectionSaveStarted = false;
            if (!response?.selectionId || readRouteSelectionId() !== true) {
                return;
            }
            writeSelectionId(response.selectionId);
        },
        () => {
            watchlistSelectionSaveStarted = false;
        },
    );
}

function createInitialQuery(): ItemsQuery {
    const selectionId = readRouteSelectionId();
    if (typeof selectionId === 'string') {
        return { selectionId };
    }
    return {
        keyword: readKeywordFromUrl(),
        ...readFilterParamsFromUrl(),
    };
}

function activeFiltersFromQuery(query: ItemsQuery): Record<string, BrowseActiveFilter> {
    const filters: Record<string, BrowseActiveFilter> = {};

    if (query.publicationDestination || query.reviewer) {
        const destination = query.publicationDestination ?? query.reviewer ?? '';
        const name = query.reviewStatus
            ? `${destination} : ${query.reviewStatus}`
            : destination;
        filters[FILTER_TYPE.PUBLISHED_TO] = {
            name,
            type: FILTER_TYPE.PUBLISHED_TO,
            itemsQuery: {
                publicationDestination: query.publicationDestination,
                reviewer: query.reviewer,
                reviewStatus: query.reviewStatus,
            },
        };
    }

    if (query.compliant === 'true') {
        filters[FILTER_TYPE.COMPLIANT_ITEMS] = {
            name: 'ITEMS_SEARCH_FILTER.MENU.COMPLIANT',
            type: FILTER_TYPE.COMPLIANT_ITEMS,
            itemsQuery: { compliant: 'true' },
            searchKey: 'compliant',
        };
    } else if (query.compliant === 'false') {
        filters[FILTER_TYPE.NON_COMPLIANT_ITEMS] = {
            name: 'ITEMS_SEARCH_FILTER.MENU.NON_COMPLIANT',
            type: FILTER_TYPE.NON_COMPLIANT_ITEMS,
            itemsQuery: { compliant: 'false' },
            searchKey: 'compliant',
        };
    }

    if (query.errorKey) {
        filters[FILTER_TYPE.VALIDATION_ERRORS] = {
            name: query.errorKey,
            type: FILTER_TYPE.VALIDATION_ERRORS,
            itemsQuery: { errorKey: query.errorKey },
            searchKey: 'errorKey',
        };
    }

    if (query.warningKey) {
        filters[FILTER_TYPE.VALIDATION_WARNINGS] = {
            name: query.warningKey,
            type: FILTER_TYPE.VALIDATION_WARNINGS,
            itemsQuery: { warningKey: query.warningKey },
            searchKey: 'warningKey',
        };
    }

    if (query.reviewErrorKey) {
        filters[FILTER_TYPE.REVIEW_ERRORS] = {
            name: query.reviewErrorKey,
            type: FILTER_TYPE.REVIEW_ERRORS,
            itemsQuery: { reviewErrorKey: query.reviewErrorKey },
            searchKey: 'reviewErrorKey',
        };
    }

    if (query.reviewWarningKey) {
        filters[FILTER_TYPE.REVIEW_WARNINGS] = {
            name: query.reviewWarningKey,
            type: FILTER_TYPE.REVIEW_WARNINGS,
            itemsQuery: { reviewWarningKey: query.reviewWarningKey },
            searchKey: 'reviewWarningKey',
        };
    }

    if (query.bulkSubscriptionId) {
        filters[FILTER_TYPE.BULK_SUBSCRIPTION] = {
            name: query.bulkSubscriptionId,
            type: FILTER_TYPE.BULK_SUBSCRIPTION,
            itemsQuery: { bulkSubscriptionId: query.bulkSubscriptionId },
        };
    }

    if (query.category) {
        filters[FILTER_TYPE.CATEGORY] = {
            name: query.category,
            type: FILTER_TYPE.CATEGORY,
            itemsQuery: { category: query.category },
        };
    }

    if (query.bulkSearchId) {
        const tags = query.tags ?? `bulkSearch:${query.bulkSearchId}`;
        filters[FILTER_TYPE.BULK_SEARCH] = {
            name: FILTER_TYPE.BULK_SEARCH,
            type: FILTER_TYPE.BULK_SEARCH,
            itemsQuery: { bulkSearchId: query.bulkSearchId, tags },
        };
    }

    return filters;
}

const KEYWORD_OWNED_FILTER_TYPES = [
    FILTER_TYPE.USER_DEFINED,
    FILTER_TYPE.DATA_MODEL_DEFINED,
] as const;

/**
 * Saved / data-model filters live in `?q=` only. Rebuilding `activeFilters`
 * from URL params would drop those pills on `$routeUpdate`. Keep a named
 * filter while the browse keyword is still set (exact match, encoding
 * differences, or FilterBuilder live preview).
 */
function mergeActiveFiltersFromUrl(
    previous: Record<string, BrowseActiveFilter>,
    urlQuery: ItemsQuery,
): Record<string, BrowseActiveFilter> {
    const fromUrl = activeFiltersFromQuery(urlQuery);
    const keyword = urlQuery.keyword ?? '';

    for (const type of KEYWORD_OWNED_FILTER_TYPES) {
        const existing = previous[type];
        if (!existing?.name || !keyword) {
            continue;
        }
        fromUrl[type] = {
            ...existing,
            itemsQuery: { ...existing.itemsQuery, keyword },
        };
    }

    return fromUrl;
}

function clearItemsQueryKeys(query: ItemsQuery, itemsQuery?: Partial<ItemsQuery>): ItemsQuery {
    if (!itemsQuery) {
        return query;
    }

    const next = { ...query };
    for (const key of Object.keys(itemsQuery) as Array<keyof ItemsQuery>) {
        delete next[key];
    }
    return next;
}

function collectUrlUpdates(
    previous: ItemsQuery,
    next: ItemsQuery,
): Partial<Record<FilterUrlKey, string | null>> | null {
    const updates: Partial<Record<FilterUrlKey, string | null>> = {};
    let changed = false;

    for (const key of FILTER_URL_KEYS) {
        const previousValue = previous[key] ?? null;
        const nextValue = next[key] ?? null;
        if (previousValue !== nextValue) {
            updates[key] = nextValue;
            changed = true;
        }
    }

    return changed ? updates : null;
}

function writeQueryToUrl(previous: ItemsQuery, next: ItemsQuery): boolean {
    let changed = false;

    if ((next.keyword ?? '') !== (previous.keyword ?? '')) {
        writeKeywordToUrl(next.keyword ?? '');
        changed = true;
    }

    const updates = collectUrlUpdates(previous, next);
    if (updates) {
        writeFilterParamsToUrl(updates);
        changed = true;
    }

    if (changed) {
        notifyAngularItemSearchQueryChanged({ ...next, keyword: next.keyword ?? '' });
    }

    return changed;
}

/**
 * Single source of truth for the browse items query passed to {@link ItemsList}.
 * Downstream filter tickets (LAX-10134, LAX-10136) attach via
 * `setActiveFilter` / `removeFilter` without forking query state.
 */
export function useBrowseQuery(): UseBrowseQueryResult {
    const [query, setQueryState] = useState<ItemsQuery>(createInitialQuery);
    const [activeFilters, setActiveFilters] = useState<Record<string, BrowseActiveFilter>>(
        () => activeFiltersFromQuery(createInitialQuery()),
    );
    const activeFiltersRef = useRef(activeFilters);
    activeFiltersRef.current = activeFilters;

    const setQuery = useCallback(
        (update: Partial<ItemsQuery> | ((previous: ItemsQuery) => ItemsQuery)) => {
            setQueryState((previous) => {
                const next =
                    typeof update === 'function'
                        ? update(previous)
                        : { ...previous, ...update };

                writeQueryToUrl(previous, next);
                return next;
            });
        },
        [],
    );

    const setActiveFilter = useCallback(
        (type: string, filter: BrowseActiveFilter) => {
            const previousFilter = activeFiltersRef.current[type];
            const exclusiveType = MUTUALLY_EXCLUSIVE_TYPES[type];
            const exclusiveFilter = exclusiveType
                ? activeFiltersRef.current[exclusiveType]
                : undefined;

            setActiveFilters((previous) => {
                const next = { ...previous };
                if (exclusiveType) {
                    delete next[exclusiveType];
                }
                next[type] = filter;
                return next;
            });

            setQuery((previousQuery) => {
                let nextQuery = clearItemsQueryKeys(previousQuery, previousFilter?.itemsQuery);
                nextQuery = clearItemsQueryKeys(nextQuery, exclusiveFilter?.itemsQuery);
                if (filter.itemsQuery) {
                    nextQuery = { ...nextQuery, ...filter.itemsQuery };
                }
                return nextQuery;
            });
        },
        [setQuery],
    );

    const removeFilter = useCallback(
        (filter: BrowseActiveFilter) => {
            setActiveFilters((previous) => {
                const next = { ...previous };
                delete next[filter.type];
                return next;
            });

            if (filter.itemsQuery) {
                const { itemsQuery } = filter;
                setQuery((previous) => clearItemsQueryKeys(previous, itemsQuery));
            }
        },
        [setQuery],
    );

    /**
     * Mirror Angular `$location.url(?tags=bulkSearch:id&bulkSearchId=id)`:
     * replace the browse query with the bulk-search params (keep category).
     */
    const applyBulkSearch = useCallback(
        (bulkSearchId: string) => {
            const tags = `bulkSearch:${bulkSearchId}`;

            setActiveFilters((previous) => {
                const next: Record<string, BrowseActiveFilter> = {};
                if (previous[FILTER_TYPE.CATEGORY]) {
                    next[FILTER_TYPE.CATEGORY] = previous[FILTER_TYPE.CATEGORY];
                }
                next[FILTER_TYPE.BULK_SEARCH] = {
                    name: FILTER_TYPE.BULK_SEARCH,
                    type: FILTER_TYPE.BULK_SEARCH,
                    itemsQuery: { bulkSearchId, tags },
                };
                return next;
            });

            setQuery((previous) => ({
                bulkSearchId,
                tags,
                category: previous.category,
            }));
        },
        [setQuery],
    );

    useEffect(() => {
        const syncQueryFromUrl = () => {
            const routeSelection = readRouteSelectionId();
            // Keep the current rows on screen while the bare flag is saved.
            // The follow-up $routeUpdate carries the real id and replaces the query.
            if (routeSelection === true) {
                saveBareWatchlistSelection();
                return;
            }
            if (typeof routeSelection === 'string') {
                setQueryState((previous) => (
                    previous.selectionId === routeSelection && Object.keys(previous).length === 1
                        ? previous
                        : { selectionId: routeSelection }
                ));
                setActiveFilters((previous) => (
                    Object.keys(previous).length === 0 ? previous : {}
                ));
                return;
            }

            const urlQuery = createInitialQuery();
            setQueryState((previous) => {
                const sameKeyword = (previous.keyword ?? '') === (urlQuery.keyword ?? '');
                const sameFilters = FILTER_URL_KEYS.every(
                    (key) => (previous[key] ?? '') === (urlQuery[key] ?? ''),
                );
                // Drop a selection id that is no longer in the route. Keyword and
                // filter keys alone would otherwise keep the watchlist query.
                if (sameKeyword && sameFilters && !previous.selectionId) {
                    return previous;
                }
                return urlQuery;
            });
            setActiveFilters((previous) => mergeActiveFiltersFromUrl(previous, urlQuery));
        };

        syncQueryFromUrl();
        window.addEventListener('popstate', syncQueryFromUrl);

        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const angularGlobal = (window as any).angular;
            const injector = angularGlobal?.element(document.body)?.injector?.();
            const $rootScope = injector?.get('$rootScope');
            if ($rootScope?.$on) {
                const off = $rootScope.$on('$routeUpdate', syncQueryFromUrl);
                return () => {
                    window.removeEventListener('popstate', syncQueryFromUrl);
                    off();
                };
            }
        } catch {
            // Storybook / unit tests: popstate only.
        }

        return () => {
            window.removeEventListener('popstate', syncQueryFromUrl);
        };
    }, []);

    return {
        query,
        setQuery,
        activeFilters,
        setActiveFilter,
        removeFilter,
        applyBulkSearch,
    };
}
